package com.eladfinish.bible.contest.screens.search.ui.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.ad_label
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.expression_to_be_searched_format
import com.eladfinish.bible.contest.search_progress_format
import com.eladfinish.bible.contest.ui.ads.SearchNativeAd
import com.eladfinish.bible.contest.ui.common.BibleSearchAnimation
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.common.accentColor
import com.eladfinish.bible.contest.ui.common.primaryLightColor
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.desc
import dev.icerock.moko.resources.format

@Composable
internal fun CmpProgressDialog(
    title: String,
    progress: Int,
    maxValue: Int,
    onCancel: (() -> Unit)?,
    modifier: Modifier = Modifier,
    message: String = "",
    showNativeAd: Boolean = false,
) {
    val normalizedProgress = when {
        maxValue <= 0 -> 0f
        progress <= 0 -> 0f
        progress >= maxValue -> 1f
        else -> progress.toFloat() / maxValue.toFloat()
    }

    AlertDialog(
        onDismissRequest = {
            onCancel?.invoke()
        },
        title = {
            SelectionContainer {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleLarge,
                )
            }
        },
        text = {
            val isShortWindow = isShortWideWindow()
            Column(
                modifier = modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Box(
                    modifier = Modifier
                        .align(Alignment.CenterHorizontally)
                        .size(if (isShortWindow) 110.dp else 190.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    // Determinate ring runs clockwise from the top (Material 3 default).
                    CircularProgressIndicator(
                        progress = { normalizedProgress },
                        modifier = Modifier.fillMaxSize(),
                        color = accentColor,
                        trackColor = primaryLightColor,
                        strokeWidth = 6.dp,
                        strokeCap = StrokeCap.Round,
                    )

                    BibleSearchAnimation(
                        modifier = Modifier.size(if (isShortWindow) 85.dp else 150.dp),
                    )
                }

                // Numeric label for the ring — sits directly beneath it, centered.
                ProgressFooter(
                    progress = progress,
                    maxValue = maxValue,
                    normalizedProgress = normalizedProgress,
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                )

                if (message.isNotEmpty()) {
                    Text(
                        text = message,
                        style = MaterialTheme.typography.bodyLarge,
                    )
                }

                if (showNativeAd) {
                    SearchNativeAd(
                        adLabel = SharedRes.strings.ad_label.desc().localized(),
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    onCancel?.invoke()
                },
            ) {
                Text(
                    text = SharedRes.strings.cancel.desc().localized(),
                )
            }
        },
        properties = DialogProperties(
            dismissOnClickOutside = false,
        ),
    )
}

@Composable
private fun ProgressFooter(
    progress: Int,
    maxValue: Int,
    normalizedProgress: Float,
    modifier: Modifier = Modifier,
) {
    val percent = (normalizedProgress * 100).toInt()

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(2.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = "$percent%",
            style = MaterialTheme.typography.titleMedium,
            color = accentColor,
        )

        Text(
            text = "$progress/$maxValue",
            style = MaterialTheme.typography.bodyMedium.copy(
                textDirection = TextDirection.Ltr,
            ),
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun CmpProgressDialogPreview() {
    var progress by remember { mutableStateOf(49) }
    PreviewTheme {
        CmpProgressDialog(
            title = SharedRes.strings.expression_to_be_searched_format.format("אברה?ם").localized(),
            message = SharedRes.strings.search_progress_format.format("בראשית", "מ\"ט", 190).localized(),
            progress = progress,
            maxValue = 929,
            onCancel = {},
        )
    }
}
